export function calcSMA(prices, period) {
  if (prices.length < period) return null;
  const slice = prices.slice(-period);
  return slice.reduce((a, b) => a + b, 0) / period;
}

export function calcEMA(prices, period) {
  if (prices.length < period) return null;
  const k = 2 / (period + 1);
  let ema = prices.slice(0, period).reduce((a, b) => a + b, 0) / period;
  for (let i = period; i < prices.length; i++) {
    ema = prices[i] * k + ema * (1 - k);
  }
  return ema;
}

/* RSI with Wilder smoothing — the definition TradingView's ta.rsi uses.
   The first average gain/loss is the simple mean of the first `period`
   changes; every later change is folded in as avg = (avg·(n−1) + x) / n.
   Pass CLOSED candles only, oldest first. With 250+ closes the seed no
   longer matters (the result matches a full-history RSI to <0.01). */
export function calcRSI(closes, period = 14) {
  if (!closes || closes.length < period + 1) return null;
  let gain = 0, loss = 0;
  for (let i = 1; i <= period; i++) {
    const d = closes[i] - closes[i - 1];
    if (d > 0) gain += d; else loss -= d;
  }
  let avgGain = gain / period;
  let avgLoss = loss / period;
  for (let i = period + 1; i < closes.length; i++) {
    const d = closes[i] - closes[i - 1];
    avgGain = (avgGain * (period - 1) + (d > 0 ? d : 0)) / period;
    avgLoss = (avgLoss * (period - 1) + (d < 0 ? -d : 0)) / period;
  }
  if (avgLoss === 0) return avgGain === 0 ? 50 : 100;
  return 100 - 100 / (1 + avgGain / avgLoss);
}

/* Descriptive RSI state — never an instruction to buy or sell. */
export function rsiState(rsi) {
  if (rsi == null) return { key: 'na', text: 'Nincs adat', color: 'var(--text3)' };
  if (rsi < 30) return { key: 'oversold', text: 'Túladott (RSI < 30)', short: 'Túladott', color: '#00e5ff' };
  if (rsi > 70) return { key: 'overbought', text: 'Túlvett (RSI > 70)', short: 'Túlvett', color: '#ff4d4d' };
  return { key: 'neutral', text: 'Semleges', short: 'Semleges', color: 'var(--text2)' };
}

/* Pearson correlation of two equally long series; null if undefined. */
export function pearson(a, b) {
  const n = Math.min(a?.length ?? 0, b?.length ?? 0);
  if (n < 3) return null;
  const x = a.slice(-n), y = b.slice(-n);
  const mx = x.reduce((s, v) => s + v, 0) / n;
  const my = y.reduce((s, v) => s + v, 0) / n;
  let sxy = 0, sxx = 0, syy = 0;
  for (let i = 0; i < n; i++) {
    const dx = x[i] - mx, dy = y[i] - my;
    sxy += dx * dy; sxx += dx * dx; syy += dy * dy;
  }
  if (sxx === 0 || syy === 0) return null;
  return sxy / Math.sqrt(sxx * syy);
}

/* Log returns of a price series (non-positive prices are skipped). */
export function logReturns(prices) {
  const out = [];
  for (let i = 1; i < (prices?.length ?? 0); i++) {
    if (prices[i] > 0 && prices[i - 1] > 0) out.push(Math.log(prices[i] / prices[i - 1]));
  }
  return out;
}

export function calcBollingerBands(prices, period = 20, stdDevMult = 2) {
  if (prices.length < period) return null;
  const slice = prices.slice(-period);
  const sma = slice.reduce((a, b) => a + b, 0) / period;
  const variance = slice.reduce((a, b) => a + Math.pow(b - sma, 2), 0) / period;
  const stdDev = Math.sqrt(variance);
  return { upper: sma + stdDevMult * stdDev, middle: sma, lower: sma - stdDevMult * stdDev };
}

export function calcMACD(prices) {
  const ema12 = calcEMA(prices, 12);
  const ema26 = calcEMA(prices, 26);
  if (ema12 === null || ema26 === null) return null;
  const macdLine = ema12 - ema26;
  return { macd: macdLine, ema12, ema26 };
}

export function calcSupportResistance(prices) {
  if (prices.length < 10) return { support: null, resistance: null };
  const recent = prices.slice(-30);
  const high = Math.max(...recent);
  const low = Math.min(...recent);
  const range = high - low;
  return {
    resistance: high,
    support: low,
    r1: high - range * 0.236,
    s1: low + range * 0.236,
    pivot: (high + low + recent[recent.length - 1]) / 3,
  };
}

export function rsiSignal(rsi) {
  if (rsi === null) return { label: 'N/A', color: '#888' };
  if (rsi >= 70) return { label: 'Overbought', color: '#ff4d4d' };
  if (rsi >= 60) return { label: 'Bullish', color: '#f5a623' };
  if (rsi >= 40) return { label: 'Neutral', color: '#aaa' };
  if (rsi >= 30) return { label: 'Bearish', color: '#4a90e2' };
  return { label: 'Oversold', color: '#00e5ff' };
}

export function trendSignal(price, sma7, sma14, sma30) {
  let bullish = 0;
  if (sma7 && price > sma7) bullish++;
  if (sma14 && price > sma14) bullish++;
  if (sma30 && price > sma30) bullish++;
  if (sma7 && sma14 && sma7 > sma14) bullish++;
  if (sma14 && sma30 && sma14 > sma30) bullish++;
  if (bullish >= 4) return { label: 'Strong Bullish', color: '#00c853' };
  if (bullish >= 3) return { label: 'Bullish', color: '#69f0ae' };
  if (bullish === 2) return { label: 'Neutral', color: '#aaa' };
  if (bullish === 1) return { label: 'Bearish', color: '#f5a623' };
  return { label: 'Strong Bearish', color: '#ff4d4d' };
}
